
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { Save,  ArrowLeft, FileArchive} from 'lucide-react';
import toast from 'react-hot-toast';

const backendUrl = 'https://justurbane.in/backend_php/public';

const AdminMagazineEditorPage = () => {

  const [magazine, setMagazine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { magazineId } = useParams();

  useEffect(() => {
    // Check admin authentication
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    console.log("MagazineID : " + magazineId);
    if (magazineId && magazineId !== 'new') {
        console.log('Feetching Details');
      fetchMagazine();
    } else {
      // New magazine
      setMagazine({
        title: '',
        description: '',
        month: '',
        year: '',
        pages: 0,
        display_pages: 1,
        published_at: '',
        articles_count: 0,
        read_time: 0,
        is_digital: false,
        is_featured: false,
        cover_image: null, // Use null initially for file
        pdf_url: null
      });
      setLoading(false);
    }
  }, [navigate, magazineId]);

  const fetchMagazine = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${backendUrl}/api/admin/digital_magazines/${magazineId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMagazine({
          ...data,
          tags: data.tags || []
        });
      } else {
        throw new Error('Failed to fetch Magazine');
      }
    } catch (error) {
      toast.error('Failed to load Magazine');
      console.error('Magazine fetch error:', error);
      navigate('/admin/digital_magazines');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!magazine.title || !magazine.description || !magazine.month || !magazine.year || !magazine.pages 
        || !magazine.display_pages || !magazine.published_at|| !magazine.articles_count || !magazine.read_time ) {
      toast.error('Please fill required fields');
      return;
    }
  
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
        
      formData.append('title', magazine.title);
      formData.append('description', magazine.description);
      formData.append('month', magazine.month);
      formData.append('year', magazine.year);
      formData.append('pages', magazine.pages);
      formData.append('display_pages', magazine.display_pages);
      formData.append('published_at', magazine.published_at);
      formData.append('articles_count', magazine.articles_count);
      formData.append('read_time', magazine.read_time);
      formData.append('is_digital', magazine.is_digital);
      formData.append('is_featured' , magazine.is_featured);
      
      if (magazine.cover_image instanceof File) {
        formData.append('cover_image', magazine.cover_image);
      }
      if (magazine.pdf_url instanceof File) {
        formData.append('pdf_url', magazine.pdf_url);
      }
  
      const url = `${backendUrl}/api/admin/digital_magazines/${magazine.id}`;
  
      const method = 'POST';
      if (magazine.id) formData.append('_method', 'PUT'); // tell backend it's an update

  
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
  
      if (response.ok) {
        const result = await response.json();
        toast.success('Magazine Updated Successfully!');
        if (!magazine.id) navigate(`/admin/digital_magazines/edit/${result.id}`);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Save failed');
      }
    } catch (error) {
      toast.error(error.message);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
  

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading magazine editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/digital_magazines')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Magazines</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-bold text-gray-900">
                {'Edit Magazine'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>
                  {saving ? 'Updating...' : 'Update'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="lg:col-span-3 space-y-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="space-y-4">

            {/* Title & Publish Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Month */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Magazine Title *
                </label>
                <input
                    type="text"
                    value={magazine?.title || ''}
                    onChange={(e) => setMagazine({ ...magazine, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Enter Magazine title..."
                />
                </div>

                {/* Publish Date */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Date *
                </label>
                <input
                    type="date"
                    value={
                    magazine?.published_at
                        ? magazine.published_at.split(' ')[0] // ✅ Extract only YYYY-MM-DD
                        : ''
                    }
                    onChange={(e) =>
                    setMagazine({ ...magazine, published_at: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                </div>

            </div>

            {/* Description */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
                </label>
                <textarea
                value={magazine?.description || ''}
                onChange={(e) => setMagazine({ ...magazine, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="Brief magazine description..."
                />
            </div>

            {/* Month & Year */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Month */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Month *
                </label>
                <input
                    type="text"
                    value={magazine?.month || ''}
                    onChange={(e) => setMagazine({ ...magazine, month: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Enter month (e.g., October)"
                />
                </div>

                {/* Year */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year *
                </label>
                <input
                    type="number"
                    value={magazine?.year || ''}
                    onChange={(e) => setMagazine({ ...magazine, year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Enter year (e.g., 2025)"
                />
                </div>
            </div>

            {/* Number of Pages & Publish Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Number of Pages */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Pages *
                </label>
                <input
                    type="number"
                    value={magazine?.pages || ''}
                    onChange={(e) => setMagazine({ ...magazine, pages: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Total pages"
                />
                </div>
                {/* Non-Subscriber Pages */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Pages to Display for Non-Subscriber *
                </label>
                <input
                type="number"
                value={magazine?.display_pages || ''}
                onChange={(e) => setMagazine({ ...magazine, display_pages: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="Enter number of pages"
                />
            </div>
                
            </div>

            {/* Articles Count & Read Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Articles Count */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Articles Count *
                </label>
                <input
                    type="number"
                    value={magazine?.articles_count || ''}
                    onChange={(e) => setMagazine({ ...magazine, articles_count: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Number of articles"
                />
                </div>

                {/* Read Time */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Read Time (in minutes) *
                </label>
                <input
                    type="number"
                    value={magazine?.read_time || ''}
                    onChange={(e) => setMagazine({ ...magazine, read_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g., 45"
                />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Cover Image */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Image
                    </label>
                    <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setMagazine({ ...magazine, cover_image: file });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    {magazine.cover_image && (
                    <div className="mt-2">
                        <img
                        src={
                            magazine.cover_image instanceof File
                            ? URL.createObjectURL(magazine.cover_image)
                            : magazine.cover_image
                        }
                        alt="Cover Preview"
                        className="h-32 object-cover rounded-lg"
                        />
                    </div>
                    )}
                </div>

                {/* PDF File */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                    PDF File
                    </label>
                    <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) setMagazine({ ...magazine, pdf_url: file });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum file size: 30MB</p>
                    {/* Clickable PDF Icon */}
                    <button
                      type="button"
                      onClick={() => {
                        const pdfLink =
                          magazine.pdf_url instanceof File
                            ? URL.createObjectURL(magazine.pdf_url)
                            : magazine.pdf_url;
                        window.open(pdfLink, '_blank');
                      }}
                      className="text-amber-600 hover:text-amber-700"
                      title="View PDF"
                    >
                      <FileArchive className="w-16 h-16" />
                    </button>
                    
                  </div>
            </div>
            </div>
        </div>
      </div>

      </div>
    </div>
  );
};

export default AdminMagazineEditorPage;